import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { DragOriginContextProvider } from "shared/context/drag-and-drop/DragOriginContext"
import { useEvent } from "shared/lib/useEvent"
import { UpdateFollowUpConfigurationInput } from "shared/types/__generated__/graphql"
import { Button } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { ProductSelectOption } from "entities/products/types/ProductSelectOption"

import { GroupEntryInput } from "../model/useFollowUpConfigurationAdminPageViewModel"
import { useFollowUpConfigurationGroupViewModel } from "../model/useFollowUpConfigurationGroupViewModel"
import { useFollowUpConfigurationReorderDnD } from "../model/useFollowUpConfigurationReorderDnD"
import { FollowUpConfigurationGroupData } from "../types/FollowUpConfigurationGroupData"
import { FollowUpConfigurationGroupAddForm } from "./FollowUpConfigurationGroupAddForm"
import { FollowUpConfigurationRowConnector } from "./FollowUpConfigurationRowConnector"

export interface FollowUpConfigurationGroupProps {
  group: FollowUpConfigurationGroupData
  productImage?: React.ReactNode
  productKind?: string
  productOptions: ProductSelectOption[]
  onUpdate: (id: string, input: UpdateFollowUpConfigurationInput) => void
  onDelete: (id: string) => Promise<void>
  onAddToGroup: (
    sourceProductName: string,
    data: GroupEntryInput
  ) => Promise<void>
  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isReorderDisabled?: boolean
}

export const FollowUpConfigurationGroup = React.memo(
  ({
    group,
    productImage,
    productKind,
    productOptions,
    onUpdate,
    onDelete,
    onAddToGroup,
    isCreating,
    isUpdating,
    isDeleting,
    isReorderDisabled
  }: FollowUpConfigurationGroupProps) => {
    const { t } = useTranslation()
    const { sourceProduct, entries } = group
    const [isAddOpen, setIsAddOpen] = useState(false)

    const { handleMove, handleReorder } =
      useFollowUpConfigurationGroupViewModel({ group, onUpdate })
    const dropHint = useFollowUpConfigurationReorderDnD({
      groupID: sourceProduct.id,
      onReorder: handleReorder
    })

    const handleOpenAdd = useEvent(() => setIsAddOpen(true))
    const handleCloseAdd = useEvent(() => setIsAddOpen(false))
    const handleAdd = useEvent((data: GroupEntryInput) =>
      onAddToGroup(sourceProduct.productName, data)
    )

    return (
      <section className="follow-up-config-admin-page__group">
        <header className="follow-up-config-admin-page__group-header">
          {productImage ? (
            <div className="follow-up-config-admin-page__group-image">
              {productImage}
            </div>
          ) : null}
          <div className="follow-up-config-admin-page__group-titles">
            <h2 className="follow-up-config-admin-page__group-title">
              {sourceProduct.productName}
            </h2>
            {productKind ? (
              <span className="follow-up-config-admin-page__group-kind">
                {productKind}
              </span>
            ) : null}
            {sourceProduct.dimensioningKey ? (
              <span className="follow-up-config-admin-page__group-meta">
                {t("DimConfiguration.Fields.DimensioningKey")}:{" "}
                {sourceProduct.dimensioningKey}
              </span>
            ) : null}
          </div>
          {isAddOpen ? null : (
            <Button
              variant="primary"
              className="follow-up-config-admin-page__group-add"
              onClick={handleOpenAdd}
              icon={IconName.PLUS_CIRCLE}
              label={t("FollowUpConfiguration.Admin.AddToGroup")}
              isIconOnly
            />
          )}
        </header>

        {/* Each group is its own drag origin so that rows can only be dropped
            between the follow-up products of the same source product. */}
        <DragOriginContextProvider origin={sourceProduct.id}>
          <div className="follow-up-config-admin-page__group-rows">
            {entries.map((entry, index) => (
              <FollowUpConfigurationRowConnector
                key={entry.id}
                entry={entry}
                groupID={sourceProduct.id}
                isFirst={index === 0}
                isLast={index === entries.length - 1}
                insertBefore={
                  dropHint?.entryID === entry.id && dropHint.insertBefore
                }
                insertAfter={
                  dropHint?.entryID === entry.id && !dropHint.insertBefore
                }
                onUpdate={onUpdate}
                onMove={handleMove}
                onDelete={onDelete}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
                isReorderDisabled={isReorderDisabled}
              />
            ))}
          </div>
        </DragOriginContextProvider>

        {isAddOpen ? (
          <FollowUpConfigurationGroupAddForm
            productOptions={productOptions}
            onAdd={handleAdd}
            onClose={handleCloseAdd}
            isCreating={isCreating}
          />
        ) : null}
      </section>
    )
  }
)

FollowUpConfigurationGroup.displayName = "FollowUpConfigurationGroup"
